import { useState, useEffect, useRef } from 'react';
import { Transition } from '@headlessui/react';
import DLoading from '@/components/DLoading';
import BubbleV3 from '@/v3/components/Bubble';
import DLoaderBubbleLoading from '@/components/Global/DLoaderBubbleLoading';
import StyleTag from '@/components/StyleTag';
import { DANTE_THEME_CHAT } from '@/constants';
import { useBubbleLoadingStore } from '@/stores/bubbleLoading/bubbleLoadingStore';
import clsx from 'clsx';

/**
 * BubbleLoadingWrapper - A component that wraps the Bubble component and handles loading states
 * 
 * This component ensures that the Bubble is only rendered when all customization data is fully loaded,
 * preventing premature rendering with default values.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.config - Configuration object for the Bubble component
 * @param {boolean} props.isLoading - Whether the customization data is still loading
 * @param {Object} props.customizationData - The customization data for the Bubble
 * @param {string} props.customizationFontFamily - Font family import URL
 * @param {boolean} props.isPreviewMode - Whether the Bubble is in preview mode
 * @param {boolean} props.isInApp - Whether the Bubble is rendered within the app
 * @param {boolean} props.initialShouldFetchCustomization - Whether to fetch customization data initially
 * @param {Object} props.slots - Slots for the Bubble component
 * @param {boolean} props.isVoiceMode - Whether the Bubble is in voice mode
 * @param {Function} props.onBackClick - Callback for back button click
 * @param {Function} props.onMenuClick - Callback for menu button click
 * @param {Function} props.onCloseClick - Callback for close button click
 * @param {Function} props.onSendMessage - Callback for message sending
 * @returns {JSX.Element} The wrapped Bubble component with loading state handling
 */
const BubbleLoadingWrapper = ({
  config,
  isLoading = false,
  customizationData = {},
  customizationFontFamily,
  isPreviewMode = false,
  isInApp = false,
  initialShouldFetchCustomization = false,
  slots,
  isVoiceMode = false,
  onBackClick,
  onMenuClick,
  onCloseClick,
  onSendMessage,
  ...rest
}) => {
  const [customizationReady, setCustomizationReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [mergedConfig, setMergedConfig] = useState(null);
  const timeoutRef = useRef(null);

  // Read content readiness from child components (e.g., HomeTabContent) via shared store
  const contentReady = useBubbleLoadingStore((state) => state.contentReady);

  // Check if home tab is enabled - if not, content loading coordination is not needed
  const homeTabEnabled = mergedConfig?.home_tab_enabled ?? config?.home_tab_enabled;

  // Unified loading: stay loading until BOTH customization AND content are ready.
  // If home tab is disabled, skip waiting for content readiness since HomeTabContent won't render.
  const loading = !customizationReady || (homeTabEnabled && !contentReady);

  // Effect to handle customization loading state based on isLoading prop and config availability
  useEffect(() => {
    // Clear any existing timeout to prevent memory leaks
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // If we're still loading or don't have required config data, maintain loading state
    if (isLoading || !config) {
      setCustomizationReady(false);
      return;
    }

    // Merge the config with customization data
    const mergedData = {
      ...config,
      ...(customizationData || {})
    };

    // Check if we have all required customization properties
    const hasRequiredCustomization =
      mergedData.brand_color &&
      mergedData.surface_color &&
      mergedData.element_color;

    // If we have all required customization, set the merged config and stop loading
    if (hasRequiredCustomization) {
      setMergedConfig(mergedData);

      // Add a small delay to ensure smooth transition
      timeoutRef.current = setTimeout(() => {
        setCustomizationReady(true);
      }, 300);
    } else if (!isPreviewMode) {
      // If we're missing required customization and not in preview mode, set error state
      setHasError(true);

      // Use fallback values after a delay
      timeoutRef.current = setTimeout(() => {
        setMergedConfig({
          ...mergedData,
          brand_color: mergedData.brand_color || DANTE_THEME_CHAT.brand_color,
          surface_color: mergedData.surface_color || DANTE_THEME_CHAT.surface_color,
          element_color: mergedData.element_color || DANTE_THEME_CHAT.element_color,
        });
        setCustomizationReady(true);
      }, 500);
    } else {
      // In preview mode, we can proceed with whatever data we have
      setMergedConfig(mergedData);

      timeoutRef.current = setTimeout(() => {
        setCustomizationReady(true);
      }, 300);
    }

    // Cleanup function to clear timeout
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isLoading, config, customizationData, isPreviewMode]);

  return (
    <>
      {/* Apply customization styles */}
      {mergedConfig && (
        <StyleTag 
          tag=".bubble" 
          tempCustomizationData={mergedConfig} 
          customImport={customizationFontFamily} 
        />
      )}

      {/* Unified loading state - visible until BOTH customization AND content are ready */}
      <Transition
        show={loading}
        enter="transition-opacity duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className={clsx(
          'absolute inset-0 z-50 flex flex-col w-full h-full items-center justify-center',
          'transition-all duration-300'
        )}>
          <DLoaderBubbleLoading
            show={true}
            elementColor={customizationData?.element_color || DANTE_THEME_CHAT.element_color}
            size={64}
          />
        </div>
      </Transition>

      {/* Bubble component - mount as soon as customization is ready so children (e.g., HomeTabContent)
          can start fetching content data. Hidden behind the loader overlay until both are ready. */}
      {customizationReady && !!mergedConfig && (
        <div className={clsx(
          'w-full h-full',
          'transition-opacity duration-300',
          'rounded-[28px]',
          loading ? 'opacity-0' : 'opacity-100'
        )}>
          <BubbleV3
            config={mergedConfig}
            accentColor={mergedConfig?.brand_color || '#5745FA'}
            chatbotName={mergedConfig?.display_name || mergedConfig?.name || 'Dante AI'}
            customIcon={mergedConfig?.custom_icon}
            className=""
            kb_id={mergedConfig?.kb_id}
            isInApp={isInApp}
            initialShouldFetchCustomization={initialShouldFetchCustomization}
            isPreviewMode={isPreviewMode}
            showInAppHeader={false}
            slots={slots}
            previous_conversation_enabled={mergedConfig?.previous_conversation_enabled}
            onBackClick={onBackClick}
            onMenuClick={onMenuClick}
            onCloseClick={onCloseClick}
            onSendMessage={onSendMessage}
          />
        </div>
      )}
    </>
  );
};

export default BubbleLoadingWrapper;
